import React, { Suspense } from 'react';
import type { LazyExoticComponent } from 'react';
import type { ActiveTokens } from '@atlaskit/tokens';
import type { IconNext, IconRemoved, IconReplaced, RenderFn } from '@atlassian/forge-ui-types';
import tokens from '@atlaskit/tokens/token-names';

import warnOnce from '../../../utils/warn-once';
import { IconGlyphs, iconRemoved, iconReplaced } from './__generated__';
import { RendererContext, FeaturePreviewContext } from '../../../context';
import { ICONOGRAPHY_REFRESH } from '../../../provider/FeaturePreviewProvider';

type Glyph = IconNext | IconRemoved | IconReplaced;

export type IconColor = Extract<ActiveTokens, `color.${string}`>;

export interface IconProps {
	color?: IconColor;
	/**
	 * Name of the icon to be rendered.
	 */
	glyph: Glyph;
	/**
	 * Text used to describe what the icon is in context. A label is needed when there is no pairing visible text next to the icon.
	 * An empty string marks the icon as presentation only.
	 */
	label: string;
	/**
	 * @deprecated the "primaryColor" prop is deprecated and will be removed on the 22nd of December 2025. Please use the "color" prop instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/icon/.
	 */
	primaryColor?: IconColor;
	/**
	 * Secondary color for the icon. Defaults to the page background for an icon that supports two colors.
	 * @deprecated the "secondaryColor" prop is deprecated and will be removed on the 22nd of December 2025. Please refactor your code to avoid using this prop.
	 */
	secondaryColor?: IconColor;
	/**
	 * There are three icon sizes: `small` (16px), `medium` (24px), and `large` (32px). However "large" is now deprecated and will be removed on the 22nd of December 2025.
	 * This pixel size refers to the canvas the icon sits on, not the size of the icon shape itself.
	 */
	size?: 'small' | 'medium' | 'large';
}

/**
 * The validateColor function and its helper functions could not be
 * moved to a separate file because it could potentially cause cyclic
 * dependencies since type IconColor must be defined here in order for
 * forge-react-types to recognise it.
 */
const iconTokenSet = new Set(Object.keys(tokens).filter((token) => token.startsWith('color')));
const isInTokenSet = (color: IconColor) => iconTokenSet.has(color);
export const validateColor = (color?: IconColor): string | undefined => {
	if (!color) {
		return undefined;
	}
	if (!isInTokenSet(color)) {
		warnOnce(`[@forge/react: Icon] "${color}" is not a valid design token for color.`);
		return undefined;
	}
	return `var(${tokens[color]})`;
};

const iconTokenSetLegacy = new Set(
	Object.keys(tokens).filter((token) => token.startsWith('color.icon')),
);
const isInTokenSetLegacy = (color: IconColor) => iconTokenSetLegacy.has(color);
export const validateColorLegacy = (color?: IconColor): string | undefined => {
	if (!color) {
		return undefined;
	}
	if (!isInTokenSetLegacy(color)) {
		warnOnce(
			`[@forge/react: Icon] the value "${color}" is not a valid design token for primaryColor or secondaryColor.`,
		);
		return undefined;
	}
	return `var(${tokens[color]})`;
};

export const IconComponent = ({
	glyph,
	label,
	color,
	size,
	primaryColor,
	secondaryColor,
}: IconProps): React.JSX.Element => {
	let Component;
	let forceSize = false;
	let spacing = 'none';

	const featurePreview = React.useContext(FeaturePreviewContext);

	// Only use the backwards compatibility logic if the feature preview is not enabled.
	if (Boolean(featurePreview[ICONOGRAPHY_REFRESH]) === false) {
		// The new icons have different dimensions:
		// medium: 24px by 24px => 16px by 16px
		// small:  16px by 16px => 12px by 12px
		// https://atlassian.design/components/icon/examples#default-16px
		// To avoid breaking changes, use the "spacing" prop to revert back to the old dimensions.
		if (size === 'small') {
			spacing = 'compact';
		}
		if (size === 'medium' || size === undefined) {
			spacing = 'spacious';
		}
	}

	// Special lookup: backwards compatibility for legacy icons
	if (size && size === 'large') {
		// @ts-ignore: we don't generate types for the legacy icons, as they are only ever used for backwards compatibility for this very use case
		Component = IconGlyphs[`LEGACY_${glyph}`] as LazyExoticComponent<any>;

		// If size="large" is used, but we don't have an icon, display the icon with the default size.
		// This would happen if a user decided to use swap the glyph for an "old" icon that is already using size="large".
		if (!Component) {
			forceSize = true;
		}
	}

	// Regular lookup
	if (!Component) {
		// @ts-ignore: TS2322: Type 'IconNext | IconRemoved | IconReplaced' is not assignable to type 'string'.
		Component = IconGlyphs[glyph] as LazyExoticComponent<any>;
	}
	const { forgeEnvironment } = React.useContext(RendererContext);

	if (forgeEnvironment !== 'PRODUCTION') {
		// Using an array avoids TS issues checking for an invalid value type
		if (size !== undefined && !['small', 'medium'].includes(size)) {
			warnOnce(
				`[@forge/react: Icon] Warning: The value "${size}" for the "size" prop has been deprecated and will be removed on the 22nd of December 2025. To avoid breaking changes, only use the values "small" or "medium". For more details, visit: https://atlassian.design/components/icon/usage#spacing-and-sizing`,
			);
		}

		if (primaryColor !== undefined) {
			warnOnce(
				`[@forge/react: Icon] Warning: The "primaryColor" is deprecated and will be removed on the 22nd of December 2025. Please use the "color" prop instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/icon/.`,
			);
		}

		if (secondaryColor !== undefined) {
			warnOnce(
				`[@forge/react: Icon] Warning: The "secondaryColor" prop has been deprecated and will be removed on the 22nd of December 2025. Please refactor your code to avoid using this prop.`,
			);
		}

		if (iconRemoved.has(glyph)) {
			warnOnce(
				`[@forge/react: Icon] The glyph "${glyph}" is deprecated and will be removed on the 22nd of December 2025. Visit https://atlassian.design/components/icon/migration-guide for information on finding a replacement.`,
			);
		}

		if (iconReplaced.has(glyph)) {
			warnOnce(
				`[@forge/react: Icon] The icon "${glyph}" has been superseded and will be removed on the 22nd of December 2025. Visit https://atlassian.design/components/icon/migration-guide for information on the recommended replacement Icon.`,
			);
		}
	}

	if (!Component) {
		warnOnce(`[@forge/react: Icon] no icon found for the glyph "${glyph}".`);
		return <></>;
	}

	return (
		<Suspense fallback={null}>
			<Component
				// @ts-ignore: Jira only - TS2322: Type '{ label: string; glyph: Glyph; color: string | undefined; spacing: "none" | "compact" | "spacious" | undefined; size: "small" | "medium" | "large" | undefined; primaryColor: string | undefined; secondaryColor: string | undefined; LEGACY_size: "small" | ... 2 more ... | undefined; LEGACY_primaryColor: string | undefi...' is not assignable to type 'IntrinsicAttributes'
				label={label}
				glyph={glyph}
				color={validateColor(color || primaryColor)}
				size={forceSize ? 'medium' : size}
				primaryColor={validateColorLegacy(color || primaryColor)}
				secondaryColor={validateColorLegacy(secondaryColor)}
				LEGACY_size={forceSize ? 'medium' : size}
				LEGACY_primaryColor={validateColorLegacy(color || primaryColor)}
				LEGACY_secondaryColor={validateColorLegacy(secondaryColor)}
				spacing={spacing}
			/>
		</Suspense>
	);
};

/**
 * An icon is a visual representation of a command, device, directory, or common action.
 */
export const Icon = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { glyph, label, color, size, primaryColor, secondaryColor } = props.forgeDoc
		.props as IconProps;

	return (
		<IconComponent
			label={label}
			glyph={glyph}
			color={color}
			size={size}
			primaryColor={primaryColor}
			secondaryColor={secondaryColor}
		/>
	);
};
